import { useState } from "react";
import { api } from "@/lib/api";
import { toggleInSet } from "./onboardShared";

/** Buy lane: registrar, keywords, TLDs and the availability / suggestion / generator results. */
export function useBuyLane({ setErr, resetSelection }) {
  const [registrar, setRegistrar] = useState("namecheap");
  const [keywords, setKeywords] = useState("");
  const [tlds, setTlds] = useState(new Set(["com", "net", "org"]));
  const [results, setResults] = useState(null);
  const [resultsSource, setResultsSource] = useState(null); // search | generate | suggest
  const [genCount, setGenCount] = useState(12);
  const [genTlds, setGenTlds] = useState(new Set(["com", "net", "ai"]));
  const [loading, setLoading] = useState(null); // search | suggest | generate | null

  function reset() { setResults(null); resetSelection(); }
  function switchRegistrar(k) { setRegistrar(k); reset(); setErr(""); }

  async function fetchResults(kind, request, emptyMsg, fallback) {
    setLoading(kind); setErr(""); reset();
    try {
      const r = await request();
      setResults(r.data.results); setResultsSource(kind);
      if (emptyMsg && !r.data.results?.length) setErr(emptyMsg);
    } catch (e) { setErr(e?.response?.data?.detail || fallback); }
    finally { setLoading(null); }
  }

  const kwList = () => keywords.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

  const doSearch = () => fetchResults("search",
    () => api.post("/domains/search-bulk", { keywords: kwList(), tlds: Array.from(tlds), registrar }), null, "Search failed");

  const doSuggest = () => fetchResults("suggest",
    () => api.post("/domains/cf-suggest", { q: keywords.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).join(" "), tlds: Array.from(tlds), limit: 30 }),
    "Cloudflare returned no suggestions for those TLDs — try other extensions or plain availability check.", "Suggestions failed");

  const doGenerate = (like = []) => fetchResults("generate",
    () => api.post("/domains/brandable", { count: Number(genCount) || 12, tlds: Array.from(genTlds), like, registrar }),
    "No available names this round — try Generate again or add more TLDs.", "Generation failed");

  return {
    registrar, switchRegistrar, keywords, setKeywords, tlds, toggleTld: (t) => setTlds((p) => toggleInSet(p, t)),
    results, resultsSource, reset, genCount, setGenCount, genTlds, toggleGenTld: (t) => setGenTlds((p) => toggleInSet(p, t)),
    searching: loading === "search", suggesting: loading === "suggest", generating: loading === "generate",
    doSearch, doSuggest, doGenerate,
  };
}

/** Import lanes: Namecheap account or Cloudflare Registrar inventory. */
export function useInventory({ setErr }) {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load(which) {
    setLoading(true); setErr("");
    try { const r = await api.get(which === "import-cf" ? "/domains/cf-inventory" : "/domains/nc-inventory"); setInventory({ ...r.data, source: which }); }
    catch (e) { setErr(e?.response?.data?.detail || "Inventory load failed"); }
    finally { setLoading(false); }
  }
  const newDomains = () => (inventory?.domains || []).filter((d) => !d.already_onboarded).map((d) => d.domain);
  return { inventory, loading, load, clear: () => setInventory(null), newDomains };
}
